import React, { useEffect, useState } from 'react';
import { Descriptions, Divider, Table, Tag, Empty } from 'antd';
import dayjs from 'dayjs';
import { WizardFormData } from './index';
import { getStaff } from '../../../../services/user';
import { getLead, Lead } from '../../../../services/lead';

interface ConfirmStepProps {
  formData: WizardFormData;
}

const ConfirmStep: React.FC<ConfirmStepProps> = ({ formData }) => {
  const [lawyers, setLawyers] = useState<any[]>([]);
  const [leadInfo, setLeadInfo] = useState<Lead | null>(null);

  useEffect(() => {
    loadLawyers();
  }, []);

  useEffect(() => {
    if (formData.basicInfo.lead_id) {
      loadLeadInfo(formData.basicInfo.lead_id);
    }
  }, [formData.basicInfo.lead_id]);

  const loadLawyers = async () => {
    try {
      const users = await getStaff({ limit: 1000 });
      setLawyers(users);
    } catch (error) {
      console.error('Failed to load lawyers:', error);
    }
  };

  const loadLeadInfo = async (leadId: number) => {
    try {
      const lead = await getLead(leadId);
      setLeadInfo(lead);
    } catch (error) {
      console.error('Failed to load lead info:', error);
    }
  };

  const getLawyerName = (id?: number) => {
    if (!id) return '-';
    const lawyer = lawyers.find((l) => l.id === id);
    return lawyer ? lawyer.real_name || lawyer.username : id.toString();
  };

  const formatDate = (date: any, format: string = 'YYYY-MM-DD') => {
    if (!date) return '-';
    if (dayjs.isDayjs(date)) return date.format(format);
    if (typeof date === 'string') return dayjs(date).format(format);
    return '-';
  };

  const partyColumns = [
    {
      title: '立场/角色',
      dataIndex: 'party_role',
      key: 'party_role',
      width: 120,
      render: (role: string) => {
        const roleMap: Record<string, string> = {
          plaintiff: '原告',
          defendant: '被告',
          client: '委托人',
          third_party: '第三人',
          enforcement_applicant: '强制执行申请人',
          enforcement_respondent: '被申请执行人',
          suspect_family: '犯罪嫌疑人家属',
          suspect: '犯罪嫌疑人',
        };
        return roleMap[role] || role || '-';
      },
    },
    {
      title: '姓名/名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '性别',
      dataIndex: 'gender',
      key: 'gender',
      width: 80,
      render: (text: string) => text || '-',
    },
    {
      title: '身份类型',
      dataIndex: 'identity_type',
      key: 'identity_type',
      width: 120,
      render: (text: string) => text || '-',
    },
    {
      title: '身份证号/统一社会信用代码',
      dataIndex: 'identity_number',
      key: 'identity_number',
      render: (text: string) => text || '-',
    },
    {
      title: '联系电话',
      dataIndex: 'phone',
      key: 'phone',
      width: 120,
      render: (text: string) => text || '-',
    },
  ];

  return (
    <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
      <Divider orientation="left">基本信息</Divider>
      <Descriptions bordered column={2} size="small">
        <Descriptions.Item label="案件名称" span={2}>
          {formData.basicInfo.case_name || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="承接日期">
          {formatDate(formData.basicInfo.accept_date)}
        </Descriptions.Item>
        <Descriptions.Item label="负责律师">
          {getLawyerName(formData.basicInfo.responsible_lawyer_id)}
        </Descriptions.Item>
        <Descriptions.Item label="助理律师" span={2}>
          {getLawyerName(formData.basicInfo.assistant_lawyer_id)}
        </Descriptions.Item>
        {formData.basicInfo.wechat_group_id && (
          <Descriptions.Item label="关联企业微信群" span={2}>
            群聊ID: {formData.basicInfo.wechat_group_id}
          </Descriptions.Item>
        )}
        {formData.basicInfo.lead_id && (
          <Descriptions.Item label="关联线索" span={2}>
            {leadInfo ? (
              <div>
                <div><strong>{leadInfo.contact_name}</strong></div>
                {leadInfo.phone && <div>手机号: {leadInfo.phone}</div>}
                {leadInfo.lead_number && <div>线索编号: {leadInfo.lead_number}</div>}
              </div>
            ) : (
              `线索ID: ${formData.basicInfo.lead_id}`
            )}
          </Descriptions.Item>
        )}
      </Descriptions>

      <Divider orientation="left">我方信息</Divider>
      {formData.ourParties.length > 0 ? (
        <Table
          columns={partyColumns}
          dataSource={formData.ourParties}
          pagination={false}
          size="small"
          rowKey={(record, index) => `our-${index}`}
        />
      ) : (
        <Empty description="暂无我方信息" />
      )}

      <Divider orientation="left">对方信息</Divider>
      {formData.otherParties.length > 0 ? (
        <Table
          columns={partyColumns}
          dataSource={formData.otherParties}
          pagination={false}
          size="small"
          rowKey={(record, index) => `other-${index}`}
        />
      ) : (
        <Empty description="暂无对方信息" />
      )}

      <Divider orientation="left">案件详情</Divider>
      <Descriptions bordered column={2} size="small">
        <Descriptions.Item label="案由">
          {formData.detail.case_reason || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="案号">
          {(formData.detail as any).case_number || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="案件类型">
          {(formData.detail as any).case_type || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="审级">
          {formData.detail.case_level || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="开庭时间">
          {formatDate((formData.detail as any).court_date, 'YYYY-MM-DD HH:mm')}
        </Descriptions.Item>
        <Descriptions.Item label="判决日期">
          {formatDate((formData.detail as any).verdict_date)}
        </Descriptions.Item>
        <Descriptions.Item label="法院名称">
          {formData.detail.court_name || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="法院联系方式">
          {formData.detail.court_contact || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="案件描述" span={2}>
          {formData.detail.case_summary || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="备注" span={2}>
          {formData.detail.notes || '-'}
        </Descriptions.Item>
      </Descriptions>
    </div>
  );
};

export default ConfirmStep;
